import _ from 'lodash'
import { AnswerSchema, BotFlow, StepSchema } from '@feedyou/schema'
import { isNotEmptyElement } from '@feedyou/utils'

import { Dialogs } from '../../components/steps/types'
import { getCorrectValue, getHeaderMainKeyIndexes } from './convertService'
import { cliWarn } from '../cliLog'

export function convertCSVToSteps(
  tree: BotFlow,
  locale: string,
  header: string[],
  csvRows: string[][]
): BotFlow {
  let prevItemKeys: string[] = []
  let prevStepId: string | undefined
  let subIndexes: { [k: string]: number } = {}

  const treeCopy = _.cloneDeep(tree)
  const dialogs: { [k: string]: any } = treeCopy.dialogs

  const {
    stepIdIndex,
    stepTypeIndex,
    dialogIdIndex,
    stepValueIndex,
    subStepIdIndex,
    subStepTypeIndex
  } = getHeaderMainKeyIndexes(header)

  csvRows.filter(isNotEmptyElement).forEach((row: string[]) => {
    const key = row[stepTypeIndex].toString().trim()
    const stepId = row[stepIdIndex].toString().trim()
    const dialogId = row[dialogIdIndex].toString().trim()
    const subItem = row[subStepIdIndex].toString().trim()
    const subKey = row[subStepTypeIndex].toString().trim()
    const value = getCorrectValue(row[stepValueIndex] || '')

    const dialog = dialogs[dialogId]

    if (!dialog) {
      cliWarn(`Dialog ${dialogId} not found!`, `-${dialogId}-`)
      return
    }

    const stepIndex = dialog.steps.findIndex((step: StepSchema) => step.id && step.id === stepId)
    const step = dialog.steps[stepIndex]

    if (!step) {
      cliWarn(`Step ${dialogId}/${stepId} not found!`)
      return
    }

    if (!key) {
      cliWarn(`Key ${key} for ${dialogId}/${stepId} not found!`)
      return
    }

    const subIndexKey = subKey.replace('carousel.button', 'carousel') + stepId + dialogId

    if (!subKey) {
      setTranslationByLocale(key, stepIndex, dialogId, dialogs, step, locale, value)
      return
    }

    const newPrevItemKeys = setBySubKey(
      key,
      subKey,
      subIndexKey,
      subIndexes,
      stepId,
      stepIndex,
      dialogId,
      dialogs,
      locale,
      step,
      value,
      prevStepId,
      prevItemKeys,
      subItem
    )

    prevStepId = stepId
    prevItemKeys = newPrevItemKeys || prevItemKeys
  })

  //@ts-ignore
  treeCopy.dialogs = dialogs

  return treeCopy
}

function setBySubKey(
  key: string,
  subKey: string,
  subIndexKey: string,
  subIndexes: { [k: string]: number },
  stepId: string,
  stepIndex: number,
  dialogId: string,
  dialogs: Dialogs,
  locale: string,
  step: StepSchema,
  translation: string,
  prevStepId: string | undefined,
  prevItemKeys: string[],
  subItem?: string
) {
  switch (subKey) {
    case 'answer':
      setAnswer(key, stepId, stepIndex, dialogId, dialogs, step, locale, translation, subItem)
      break
    case 'carousel':
      return setCarousel(
        key,
        stepId,
        stepIndex,
        subIndexKey,
        subIndexes,
        dialogId,
        dialogs,
        step,
        locale,
        translation,
        prevStepId,
        prevItemKeys
      )
    case 'carousel.button': {
      setCarouselButton(
        key,
        stepId,
        stepIndex,
        subIndexKey,
        subIndexes,
        dialogId,
        dialogs,
        step,
        locale,
        translation
      )
      break
    }
    default:
      break
  }
}

function setTranslationByLocale(
  key: string,
  stepIndex: number,
  dialogId: string,
  dialogs: Dialogs,
  step: StepSchema,
  locale: string,
  translation: string
) {
  if (locale === 'default') {
    dialogs[dialogId].steps[stepIndex][key] = translation
    return
  }

  dialogs[dialogId].steps[stepIndex].mutations = mutate(
    step.mutations || [],
    locale,
    key,
    translation
  )
}

function setCarousel(
  key: string,
  stepId: string,
  stepIndex: number,
  subIndexKey: string,
  subIndexes: { [k: string]: number },
  dialogId: string,
  dialogs: Dialogs,
  step: StepSchema,
  locale: string,
  translation: string,
  prevStepId: string | undefined,
  prevItemKeys: string[]
) {
  // aktualni item obsahuje znovu key, takze uz jde nejspis o dalsi item
  const { itemIndex, prevItemKeys: newPrevItemKeys } = setPrevItemKeys(
    `${dialogId}:${key}`,
    stepId,
    subIndexKey,
    subIndexes,
    prevStepId,
    prevItemKeys
  )

  if (!step.carousel || !step.carousel[itemIndex]) {
    cliWarn(`Not found carousel item index ${itemIndex} in ${dialogId}/${stepId}`)
    return newPrevItemKeys
  }

  if (locale === 'default') {
    dialogs[dialogId].steps[stepIndex].carousel[itemIndex][key] = translation
    return newPrevItemKeys
  }

  dialogs[dialogId].steps[stepIndex].carousel[itemIndex].mutations = mutate(
    step.carousel[itemIndex].mutations,
    locale,
    key,
    translation
  )

  return newPrevItemKeys
}

function setPrevItemKeys(
  key: string,
  stepId: string,
  subIndexKey: string,
  subIndexes: { [k: string]: number },
  prevStepId: string | undefined,
  prevItemKeys: string[]
) {
  if (stepId !== prevStepId) {
    prevItemKeys = [key]

    return { prevItemKeys, itemIndex: subIndexes[subIndexKey] || 0 }
  }

  if (prevItemKeys.includes(key)) {
    prevItemKeys = [key]
    subIndexes[subIndexKey] = (subIndexes[subIndexKey] || 0) + 1

    return { prevItemKeys, itemIndex: subIndexes[subIndexKey] }
  }

  prevItemKeys.push(key)

  return { prevItemKeys, itemIndex: subIndexes[subIndexKey] || 0 }
}

function setCarouselButton(
  key: string,
  stepId: string,
  stepIndex: number,
  subIndexKey: string,
  subIndexes: { [k: string]: number },
  dialogId: string,
  dialogs: Dialogs,
  step: StepSchema,
  locale: string,
  translation: string
) {
  const itemIndex = subIndexes[subIndexKey] || 0
  const buttonIndex =
    (subIndexes[subIndexKey + itemIndex + 'button'] =
      (subIndexes[subIndexKey + itemIndex + 'button'] || 0) + 1) - 1

  if (
    !step.carousel ||
    !step.carousel[itemIndex] ||
    !step.carousel[itemIndex].buttons[buttonIndex]
  ) {
    cliWarn(
      `Not found carousel button index ${itemIndex}/${buttonIndex} in ${dialogId}/${stepId} - ${translation}`
    )
    return
  }

  if (locale === 'default') {
    dialogs[dialogId].steps[stepIndex].carousel[itemIndex].buttons[buttonIndex][key] = translation
    return
  }

  dialogs[dialogId].steps[stepIndex].carousel[itemIndex].buttons[buttonIndex].mutations = mutate(
    step.carousel[itemIndex].buttons[buttonIndex].mutations,
    locale,
    key,
    translation
  )
}

function setAnswer(
  key: string,
  stepId: string,
  stepIndex: number,
  dialogId: string,
  dialogs: Dialogs,
  step: StepSchema,
  locale: string,
  translation: string,
  subItem?: string
): void {
  const answerIndex = getAnswerIndex(step, subItem)

  if (answerIndex < 0) {
    cliWarn(`Not found answer in ${dialogId}/${stepId} #${subItem}`)
    return
  }

  if (locale === 'default') {
    dialogs[dialogId].steps[stepIndex].answers[answerIndex][key] = translation
    return
  }

  dialogs[dialogId].steps[stepIndex].answers[answerIndex].mutations = mutate(
    step.answers[answerIndex].mutations,
    locale,
    key,
    translation
  )
}

function getAnswerIndex(step: StepSchema, subItem?: string): number {
  if (!subItem) {
    return 0
  }

  return step.answers.findIndex((answer: AnswerSchema) => answer.id && answer.id === subItem)
}

function mutate(mutations: any[], locale: string, key: string, translation: string) {
  const oldMutationIndex = (mutations || []).filter(
    (m: any) =>
      m.test && m.test.length === 1 && m.test[0].field === 'locale' && m.test[0].value === locale
  )[0]

  const oldMutation = mutations && mutations[oldMutationIndex]

  if (!translation) cliWarn('empty translation', locale, key)

  const newMutation = {
    test: [{ field: 'locale', value: locale }],
    set: [...(oldMutation ? oldMutation.set : []), { field: key, value: translation.trim() }]
  }

  const filteredMutations = (mutations || []).filter((_, i) => i !== oldMutationIndex)

  return [...filteredMutations, newMutation]
}
